import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View, Dimensions } from "react-native";
import { Title16, Title13, Title18, Title22, Title30 } from "@stylesheets";
import { Colors } from "@constants";
import { I18n } from "react-redux-i18n";
import { CircleAdd, Information } from "@assets";
import { useDispatch, useSelector } from "react-redux";
import { ForceInfoAppUpdateActions, GlobalState, LoaderActions } from "@redux";
import { HoldingView, Token, UserProfile } from "@foodi/core";
import { TestIDs } from "@utils";
import { useDevices } from "@hooks";
import { HoldingThunks, HoldingViewModel, FiltersActions, PreferredZonesThunks, ProfileScreens, AccountActions } from "@modules";
import _ from "lodash";
import {
  HoldingViewDeleteModal,
  HoldingViewModal,
  HoldingViewRow,
  ModalTemplate,
} from "@atomic";
import { NavigationProp } from "@react-navigation/native";
import { getApolloClient } from "../../../apollo";

interface IProps {
  navigation: NavigationProp<any>;
}

export const HoldingViewList: React.FC<IProps> = React.memo(
  ({ navigation }) => {
    const dispatch = useDispatch();
    const [isMobile] = useDevices();

    const [holdingViews, setHoldingViews] = useState<HoldingView[]>([]);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [currentHoldingView, setCurrentHoldingView] = useState<
    HoldingView | undefined
    >();
    const [nextHoldingView, setNextHoldingView] = useState<
    HoldingView | undefined
    >();
    const [updateHoldingViews, setUpdateHoldingViews] = useState<boolean>(
      false
    );

    const [showPreferredZonesModal, setShowPreferredZonesModal] = useState<boolean>(false);
    const [isActiveHoldingView , setIsActiveHoldingView] = useState<boolean>(false);

    const Title: React.FC<any> = isMobile ? Title22 : Title30;
    const Info: React.FC<any> = isMobile ? Title18 : Title22;
    const HoldingViewRowText: React.FC<any> = isMobile ? Title13 : Title18;

    const { idHoldingView, isTermsAccepted } =
    useSelector((state: GlobalState) => state.auth?.userInfo) ??
    ({} as UserProfile);

    const styles = useMemo(() => _styles(isMobile, Boolean(isTermsAccepted)), [isMobile, isTermsAccepted]);

    const { accessToken } =
    useSelector((state: GlobalState) => state.auth?.authToken) ??
    ({} as Token);

    const holdingViewModel = new HoldingViewModel(dispatch, isMobile);

    const setSortedHoldingViews = async () => {
      dispatch(LoaderActions.setLoading(true));
      const sortedHoldingViews = await holdingViewModel.getSortedHoldingViews(
        accessToken,
        idHoldingView
      );
      setHoldingViews(sortedHoldingViews);
      if (sortedHoldingViews.length === 1)
        checkDefaultHolding(sortedHoldingViews);
      dispatch(LoaderActions.setLoading(false));
    };

    React.useEffect(() => {
      if (accessToken) setSortedHoldingViews();
    }, [updateHoldingViews, idHoldingView]);

    const handleAddHoldingView = async () => {
      await dispatch(AccountActions.setSuccessfullyAddedHolding(false));
      navigation.navigate(ProfileScreens.BADGE_SETUP)
    };

    const checkDefaultHolding = (sortedHoldingViews: HoldingView[]) => {
      const isActiveHoldingView = holdingViewModel.isActiveHoldingView(
        idHoldingView,
        sortedHoldingViews[0]?.id
      );
      if (!isActiveHoldingView)
        handleHoldingViewSwitch(sortedHoldingViews[0])();
    };

    const handleClose = (update?: boolean, nextHV?: HoldingView) =>
      /* istanbul ignore next */
      async () => {
        if (nextHV) await handleHoldingViewSwitch(nextHV)();
        if (update) {
          await refreshAppInfo();
          setUpdateHoldingViews(!updateHoldingViews);
        }
        if(showPreferredZonesModal) {
          setShowPreferredZonesModal(false)
        } else {
          setIsOpen(false);
        }
      };

    const handleHoldingViewSwitch = (hv: HoldingView) =>
      /* istanbul ignore next */
      async () => {
        const result = await dispatch(
          HoldingThunks.switchUserHoldingView({
            idHolding: hv?.holding?.id,
            guestLastName: hv?.guest?.lastName,
            serialNumber:
              hv?.guest?.serialNumber || hv?.guest?.supportSerialNumber,
            idHoldingView: hv.id,
          })
        );
        if (nextHoldingView) setNextHoldingView(undefined);
        //@ts-ignore
        if (result) {
          refreshAppInfo();
        }
      };

    const refreshAppInfo = async () => {
      dispatch(LoaderActions.setLoading(true));
      dispatch(PreferredZonesThunks.resetPreferredZonesStore());
      dispatch(FiltersActions.resetState());
      await getApolloClient().cache.reset();
      dispatch(ForceInfoAppUpdateActions.setForceAppUpdate(true));
    };

    const handleModifyHoldingView = (hv: HoldingView) =>
      async () => {
        await dispatch(AccountActions.setSuccessfullyAddedHolding(false));
        navigation.navigate(ProfileScreens.BADGE_SETUP, { holdingView: hv })
      };

    const handleDeleteHoldingView = (
      hv: HoldingView,
      index: number,
      isActiveHoldingView?: boolean
    ) =>
      /* istanbul ignore next */
      () => {
        if (holdingViews?.[index] && isActiveHoldingView)
          setNextHoldingView(holdingViews?.[index]);

        setIsActiveHoldingView(
          holdingViewModel.isActiveHoldingView(idHoldingView, hv.id)
        )
        setCurrentHoldingView(hv);
        setDeleteModal(true);
        setIsOpen(true);
      };

    const handleModalClose = (nextHV?: HoldingView) =>
      /* istanbul ignore next */
      (update?: boolean) => handleClose(update, nextHV)();

    return (
      <>
        <View style={styles.container}>
          <Title isBlack isBold>
            {I18n.t("profile.holdingAndBadge")}
          </Title>
          <Title16>{I18n.t("profile.holdingViewDescription")}</Title16>
          <View style={styles.holdingViewsContainer}>
            {_.isEmpty(holdingViews) ? (
              <View style={styles.emptyHoldingViews}>
                <HoldingViewRowText style={styles.holdingView} isBold bold>
                  {holdingViewModel.getHoldingViewRowText()}
                </HoldingViewRowText>
              </View>
            ) : (
              <>
                {holdingViews.map((hv: HoldingView, index: number) => (
                  <HoldingViewRow
                    navigation={navigation}
                    hv={hv}
                    index={index}
                    handleModifyHoldingView={handleModifyHoldingView}
                    handleDeleteHoldingView={handleDeleteHoldingView}
                    handleHoldingViewSwitch={handleHoldingViewSwitch}
                  />
                ))}
                <View style={[styles.row, styles.marginBottom]}>
                  <View style={styles.iconContainer}>
                    <Information />
                  </View>
                  <Title16>{I18n.t("account.holdingViewInformation")}</Title16>
                </View>
              </>
            )}
          </View>
          <View style={styles.row}>
            <Pressable
              testID={TestIDs.profile.actions.pressAddHoldingView}
              disabled={!isTermsAccepted}
              style={styles.iconContainerAdd}
              onPress={handleAddHoldingView}
            >
            <CircleAdd />
            <Info isBlack isNotBold style={styles.textAdd}>
              {I18n.t("account.add")}
            </Info>
            </Pressable>
          </View>
        </View>
        <ModalTemplate
          forwardCloseTestID={TestIDs.profile.actions.pressholdingViewModal}
          isOpen={isOpen}
          handleClose={handleClose(false, undefined)}
          isMobile={isMobile}
          isFullScreen={!deleteModal}
          closeOnClickOutside
          isCenter
          bodyStyle={deleteModal ? {} : styles.modalBody}
          style={deleteModal ? {} : styles.modal}
        >
          {deleteModal ? (
            <HoldingViewDeleteModal
              handleClose={handleModalClose(nextHoldingView)}
              currentHoldingView={currentHoldingView}
            />
          ) : (
            <HoldingViewModal
              handleClose={handleModalClose(undefined)}
              currentHoldingView={currentHoldingView}
              showPreferredZonesModal={showPreferredZonesModal}
              setShowPreferredZonesModal={setShowPreferredZonesModal}
              isActiveHoldingView={isActiveHoldingView}
            />
          )}
        </ModalTemplate>
      </>
    );
  }
);

const _styles = (isMobile: boolean, isTermsAccepted: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      borderWidth: isMobile ? 0 : 1,
      borderRadius: 8,
      borderColor: Colors.darkGrey,
      justifyContent: "flex-start",
      alignItems: "flex-start",
      paddingHorizontal: isMobile ? 0 : 40,
      paddingVertical: isMobile ? 0 : 20,
      marginBottom: isMobile ? 0 : 20,
    },
    holdingViewsContainer: {
      marginTop: 17,
    },
    holdingView: {
      backgroundColor: Colors.disabledBackground,
      borderRadius: 15,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderWidth: 2,
      borderColor: Colors.transparent,
      flexDirection: "row",
    },
    row: {
      flexDirection: "row"
    },
    iconContainerAdd: {
      marginRight: 10,
      flexDirection: "row",
      alignItems: "center",
      opacity: isTermsAccepted ? 1 : 0.5,
    },
    textAdd: {
      marginLeft: 10
    },
    iconContainer: {
      marginRight: 10,
    },
    marginBottom: {
      marginBottom: 16,
    },
    emptyHoldingViews: {
      marginBottom: 20,
    },
    modal: {
      width: isMobile ? Dimensions.get("window").width : "auto",
      minHeight: isMobile ? 'auto' : '600px'
    },
    modalBody: {
      maxWidth: isMobile ? Dimensions.get("window").width : "50%",
    },
  });
